import { useEffect, useRef } from 'react';
import { getEcho, boardChannelName } from '../services/echo';
import {
  CARD_EVENT_NAMES,
  CardCreatedEvent,
  CardUpdatedEvent,
  CardMovedEvent,
  CardDeletedEvent,
} from '../services/cardEvents';
import {
  COLUMN_EVENT_NAMES,
  ColumnCreatedEvent,
  ColumnUpdatedEvent,
  ColumnDeletedEvent,
} from '../services/columnEvents';
import {
  COMMENT_EVENT_NAMES,
  CommentCreatedEvent,
  CommentUpdatedEvent,
  CommentDeletedEvent,
} from '../services/commentEvents';
import {
  BOARD_MEMBER_EVENT_NAMES,
  BoardMemberAddedEvent,
  BoardMemberRoleChangedEvent,
  BoardMemberRemovedEvent,
} from '../services/boardMemberEvents';
import {
  ATTACHMENT_EVENT_NAMES,
  AttachmentAddedEvent,
  AttachmentDeletedEvent,
} from '../services/attachmentEvents';
import { CARD_ACTIVITY_EVENT_NAMES, CardActivityCreatedEvent } from '../services/cardActivityEvents';

export interface BoardChannelHandlers {
  onCardCreated?: (event: CardCreatedEvent) => void;
  onCardUpdated?: (event: CardUpdatedEvent) => void;
  onCardMoved?: (event: CardMovedEvent) => void;
  onCardDeleted?: (event: CardDeletedEvent) => void;
  onColumnCreated?: (event: ColumnCreatedEvent) => void;
  onColumnUpdated?: (event: ColumnUpdatedEvent) => void;
  onColumnDeleted?: (event: ColumnDeletedEvent) => void;
  onCommentCreated?: (event: CommentCreatedEvent) => void;
  onCommentUpdated?: (event: CommentUpdatedEvent) => void;
  onCommentDeleted?: (event: CommentDeletedEvent) => void;
  onBoardMemberAdded?: (event: BoardMemberAddedEvent) => void;
  onBoardMemberRoleChanged?: (event: BoardMemberRoleChangedEvent) => void;
  onBoardMemberRemoved?: (event: BoardMemberRemovedEvent) => void;
  onAttachmentAdded?: (event: AttachmentAddedEvent) => void;
  onAttachmentDeleted?: (event: AttachmentDeletedEvent) => void;
  onCardActivityCreated?: (event: CardActivityCreatedEvent) => void;
}

export const useBoardChannel = (
  boardId: number | null | undefined,
  handlers?: BoardChannelHandlers
): void => {
  // Réf, pas dépendance d'effet : évite de réabonner si les callbacks ne sont pas mémoïsées.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!boardId) {
      return;
    }

    const channelName = boardChannelName(boardId);
    const channel = getEcho().private(channelName);

    channel.listen(CARD_EVENT_NAMES.created, (event: CardCreatedEvent) =>
      handlersRef.current?.onCardCreated?.(event)
    );
    channel.listen(CARD_EVENT_NAMES.updated, (event: CardUpdatedEvent) =>
      handlersRef.current?.onCardUpdated?.(event)
    );
    channel.listen(CARD_EVENT_NAMES.moved, (event: CardMovedEvent) =>
      handlersRef.current?.onCardMoved?.(event)
    );
    channel.listen(CARD_EVENT_NAMES.deleted, (event: CardDeletedEvent) =>
      handlersRef.current?.onCardDeleted?.(event)
    );
    channel.listen(COLUMN_EVENT_NAMES.created, (event: ColumnCreatedEvent) =>
      handlersRef.current?.onColumnCreated?.(event)
    );
    channel.listen(COLUMN_EVENT_NAMES.updated, (event: ColumnUpdatedEvent) =>
      handlersRef.current?.onColumnUpdated?.(event)
    );
    channel.listen(COLUMN_EVENT_NAMES.deleted, (event: ColumnDeletedEvent) =>
      handlersRef.current?.onColumnDeleted?.(event)
    );
    channel.listen(COMMENT_EVENT_NAMES.created, (event: CommentCreatedEvent) =>
      handlersRef.current?.onCommentCreated?.(event)
    );
    channel.listen(COMMENT_EVENT_NAMES.updated, (event: CommentUpdatedEvent) =>
      handlersRef.current?.onCommentUpdated?.(event)
    );
    channel.listen(COMMENT_EVENT_NAMES.deleted, (event: CommentDeletedEvent) =>
      handlersRef.current?.onCommentDeleted?.(event)
    );
    channel.listen(BOARD_MEMBER_EVENT_NAMES.added, (event: BoardMemberAddedEvent) =>
      handlersRef.current?.onBoardMemberAdded?.(event)
    );
    channel.listen(BOARD_MEMBER_EVENT_NAMES.roleChanged, (event: BoardMemberRoleChangedEvent) =>
      handlersRef.current?.onBoardMemberRoleChanged?.(event)
    );
    channel.listen(BOARD_MEMBER_EVENT_NAMES.removed, (event: BoardMemberRemovedEvent) =>
      handlersRef.current?.onBoardMemberRemoved?.(event)
    );
    channel.listen(ATTACHMENT_EVENT_NAMES.added, (event: AttachmentAddedEvent) =>
      handlersRef.current?.onAttachmentAdded?.(event)
    );
    channel.listen(ATTACHMENT_EVENT_NAMES.deleted, (event: AttachmentDeletedEvent) =>
      handlersRef.current?.onAttachmentDeleted?.(event)
    );
    channel.listen(CARD_ACTIVITY_EVENT_NAMES.created, (event: CardActivityCreatedEvent) =>
      handlersRef.current?.onCardActivityCreated?.(event)
    );

    return () => {
      getEcho().leave(channelName);
    };
  }, [boardId]);
};
